import qs from 'qs';
import { formatRule, formatRuleVariant } from '@/search-for-retail/rule/rule.formatters';
import { parseRule } from '@/search-for-retail/rule/rule.parsers';
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
/** Path builder for the rule resource under an area. */
const RULE_PATH = 'rule';
/** Maps the bulk-conflicts DTO to a record keyed by targetRuleId. Exported for unit testing. */
export const transformBulkConflictsResponse = (response) => {
    var _a;
    return Object.fromEntries(((_a = response === null || response === void 0 ? void 0 : response.conflicts) !== null && _a !== void 0 ? _a : []).map(({ targetRuleId, conflictingRules }) => [targetRuleId, conflictingRules !== null && conflictingRules !== void 0 ? conflictingRules : []]));
};
/**
 * RTK Query endpoints for rule data.
 *
 * This module extends the base ccApi with rule query, mutation, and
 * promotion endpoints.
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const ruleApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Fetches the list of rules for a given area.
         *
         * @endpoint GET /{areaId}/rule
         * @returns Parsed Rule[] via transformResponse
         */
        getRuleList: builder.query({
            query: ({ areaId }) => `/${areaId}/${RULE_PATH}`,
            transformResponse: (response) => response.map(parseRule),
            providesTags: [CC_API_TAGS.RuleList],
        }),
        /**
         * Fetches a single rule by ID within an area.
         *
         * @endpoint GET /{areaId}/rule/{id}
         * @returns Parsed Rule via transformResponse
         */
        getRuleById: builder.query({
            query: ({ areaId, id }) => `/${areaId}/${RULE_PATH}/${id}`,
            transformResponse: (response) => parseRule(response),
            providesTags: (_result, _error, { id }) => [{ type: CC_API_TAGS.Rule, id }],
        }),
        /**
         * Creates a new rule in the given area.
         *
         * @endpoint POST /{areaId}/rule
         * @returns Parsed Rule via transformResponse
         */
        createRule: builder.mutation({
            query: ({ areaId, data }) => ({
                url: `/${areaId}/${RULE_PATH}`,
                method: 'POST',
                body: formatRule(Object.assign({ areaId }, data)),
            }),
            transformResponse: (response) => parseRule(response),
            invalidatesTags: [CC_API_TAGS.RuleList],
        }),
        /**
         * Updates an existing rule, including lifecycle transitions (Draft/Archive/Publish via `status`).
         *
         * @endpoint PUT /{areaId}/rule/{id}
         * @returns Parsed Rule via transformResponse
         */
        updateRule: builder.mutation({
            query: ({ data }) => ({
                url: `/${data.areaId}/${RULE_PATH}/${data.id}`,
                method: 'PUT',
                body: formatRule(data),
            }),
            transformResponse: (response) => parseRule(response),
            invalidatesTags: (_result, _error, { data }) => [
                CC_API_TAGS.RuleList,
                { type: CC_API_TAGS.Rule, id: data.id },
            ],
        }),
        /**
         * Deletes a single rule from an area.
         *
         * @endpoint DELETE /{areaId}/rule/{id}
         */
        deleteRule: builder.mutation({
            query: ({ areaId, id }) => ({
                url: `/${areaId}/${RULE_PATH}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { id }) => [
                CC_API_TAGS.RuleList,
                { type: CC_API_TAGS.Rule, id },
            ],
        }),
        /**
         * Bulk-deletes rules from an area.
         *
         * @endpoint DELETE /{areaId}/rule/bulk/delete
         * @returns The list of successfully deleted rules
         */
        bulkDeleteRule: builder.mutation({
            query: ({ areaId, ids }) => {
                const query = qs.stringify({ ids }, { indices: false });
                return {
                    url: `/${areaId}/${RULE_PATH}/bulk/delete?${query}`,
                    method: 'DELETE',
                };
            },
            invalidatesTags: [CC_API_TAGS.RuleList],
        }),
        /**
         * Stops an experiment on a rule by selecting a winning variant.
         *
         * @endpoint PUT /{areaId}/rule/{ruleId}/experiment/stop
         * @returns Parsed Rule via transformResponse
         */
        stopExperiment: builder.mutation({
            query: ({ areaId, ruleId, winnerVariant }) => ({
                url: `/${areaId}/${RULE_PATH}/${ruleId}/experiment/stop`,
                method: 'PUT',
                body: formatRuleVariant(winnerVariant),
            }),
            transformResponse: (response) => parseRule(response),
            invalidatesTags: (_result, _error, { ruleId }) => [
                CC_API_TAGS.RuleList,
                { type: CC_API_TAGS.Rule, id: ruleId },
            ],
        }),
        /**
         * Promotes a rule to another area (promotion flow).
         *
         * @endpoint POST /{areaId}/rule/{ruleId}/promotion/{targetAreaId}?force=...
         */
        promotionRuleToArea: builder.mutation({
            query: ({ areaId, ruleId, targetAreaId, force, }) => {
                const query = qs.stringify({ force: String(force) });
                return {
                    url: `/${areaId}/${RULE_PATH}/${ruleId}/promotion/${targetAreaId}?${query}`,
                    method: 'POST',
                };
            },
            invalidatesTags: [CC_API_TAGS.RuleList],
        }),
        /**
         * Bulk-promotes rules to another area.
         *
         * @endpoint POST /{areaId}/rule/bulk/promotion/{targetAreaId}?ids=...&force=true
         */
        bulkPromoteRuleToArea: builder.mutation({
            query: ({ areaId, targetAreaId, ruleIds }) => {
                const query = qs.stringify({ ids: ruleIds, force: String(true) }, { indices: false });
                return {
                    url: `/${areaId}/${RULE_PATH}/bulk/promotion/${targetAreaId}?${query}`,
                    method: 'POST',
                };
            },
            invalidatesTags: [CC_API_TAGS.RuleList],
        }),
        /**
         * Checks for trigger conflicts on a rule (transient read via POST).
         *
         * @endpoint POST /{areaId}/rule/conflicts
         * @returns Flattened conflicting rules array via transformResponse
         */
        checkRuleConflicts: builder.mutation({
            query: ({ areaId, data }) => ({
                url: `/${areaId}/${RULE_PATH}/conflicts`,
                method: 'POST',
                body: formatRule(Object.assign({ areaId }, data)),
            }),
            transformResponse: (response) => (response === null || response === void 0 ? void 0 : response.conflictingRules) || [],
        }),
        /**
         * Checks for trigger conflicts across multiple rules in bulk.
         *
         * @endpoint POST /{areaId}/rule/bulk-conflicts
         * @returns Map of targetRuleId → conflicting rule sets via transformResponse
         */
        checkBulkRuleConflicts: builder.mutation({
            query: ({ areaId, ruleIds }) => ({
                url: `/${areaId}/${RULE_PATH}/bulk-conflicts`,
                method: 'POST',
                body: { ruleIds },
            }),
            transformResponse: transformBulkConflictsResponse,
            // No invalidatesTags - this is a read-like operation.
        }),
        /**
         * Merges build facets (transient read via POST).
         *
         * @endpoint POST /{areaId}/rule/merge-facets
         */
        mergeFacets: builder.mutation({
            query: ({ areaId, data }) => ({
                url: `/${areaId}/${RULE_PATH}/merge-facets`,
                method: 'POST',
                body: data,
            }),
            // No invalidatesTags - this is a read-like operation.
        }),
        /**
         * Fetches validation/promotion info for a rule against a target area.
         *
         * @endpoint GET /{areaId}/rule/{ruleId}/promotion/{targetAreaId}
         */
        getValidationRulePromotionInfo: builder.query({
            query: ({ areaId, ruleId, targetAreaId }) => (`/${areaId}/${RULE_PATH}/${ruleId}/promotion/${targetAreaId}`),
        }),
        /**
         * Fetches the raw RuleDTO for a rule by ID (no parsing/transform).
         *
         * @endpoint GET /{areaId}/rule/{id}
         * @returns Raw RuleDTO — distinct from getRuleById which returns a parsed Rule
         */
        getRuleDTOById: builder.query({
            query: ({ areaId, id }) => `/${areaId}/${RULE_PATH}/${id}`,
        }),
    }),
});
export const { useBulkDeleteRuleMutation, useBulkPromoteRuleToAreaMutation, useCheckBulkRuleConflictsMutation, useCheckRuleConflictsMutation, useCreateRuleMutation, useDeleteRuleMutation, useGetRuleByIdQuery, useGetRuleListQuery, useLazyGetRuleDTOByIdQuery, useLazyGetRuleListQuery, useLazyGetValidationRulePromotionInfoQuery, useMergeFacetsMutation, usePromotionRuleToAreaMutation, useStopExperimentMutation, useUpdateRuleMutation, } = ruleApi;
